import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import openSocket from "../../services/socket-io";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo, { TicketTagsBar } from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import TicketMediaGallery from "../TicketMediaGallery";
import ScheduledMessageModal from "../ScheduledMessageModal";
import { Button } from "../ui/button";
import { Files, Clock, Send, Loader2 } from "lucide-react";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ChatOptimisticProvider } from "../../context/ChatOptimistic/ChatOptimisticContext";
import toastError from "../../errors/toastError";
import useTecladoVirtual from "../../hooks/useTecladoVirtual";
import { touchTicket } from "../../services/recentContext";

const Ticket = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [galeriaAberta, setGaleriaAberta] = useState(false);
  // Controla o modal de agendamento aberto a partir do aviso "conversa
  // agendada" (ver/editar o que está marcado para este atendimento).
  const [agendamentoAberto, setAgendamentoAberto] = useState(false);
  // Enquanto o "Enviar agora" da faixa está em andamento — trava o botão e
  // mostra o giro em vez do ícone.
  const [enviandoAgendamentoAgora, setEnviandoAgendamentoAgora] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  // Contato do atendimento aberto, sempre atualizado para o ouvinte do socket
  // (que é registrado uma vez) comparar sem depender de valor "congelado".
  const contactIdAtualRef = useRef(null);
  useEffect(() => {
    contactIdAtualRef.current = ticket?.contactId ?? null;
  }, [ticket?.contactId]);

  // Releitura silenciosa: a conversa já está na tela, então nada de esqueleto
  // nem toast de erro — é sincronização de fundo (ex.: um agendamento mudou).
  const recarregarTicket = useCallback(async () => {
    try {
      const { data } = await api.get("/tickets/" + ticketId);
      setContact(data.contact);
      setTicket(data);
    } catch (_) {
      // Silencioso de propósito.
    }
  }, [ticketId]);

  // Dispara o agendamento deste atendimento na hora, sem esperar a hora
  // marcada. É o mesmo envio do job automático (o backend reusa o serviço),
  // então o que sai pelo botão é idêntico ao que sairia sozinho.
  const handleEnviarAgendamentoAgora = async () => {
    const id = ticket.agendamentoPendenteId;
    if (!id) {
      toast.error("Não encontrei o agendamento deste atendimento.");
      return;
    }
    setEnviandoAgendamentoAgora(true);
    try {
      await api.post(`/scheduled-messages/${id}/enviar-agora`);
      toast.success("Mensagem enviada agora!");
      // O agendamento deixa de ser pendente: a faixa some no recarregamento.
      await recarregarTicket();
    } catch (err) {
      toastError(err);
    }
    setEnviandoAgendamentoAgora(false);
  };

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, navigate]);

  useEffect(() => {
    if (ticket?.id && !loading) {
      touchTicket(ticket);
    }
  }, [ticket?.id, ticket?.lastMessage, loading]);

  useEffect(() => {
    const socket = openSocket();

    // Join duplo: (1) IMEDIATO se socket já connected (navegação após a 1ª
    // página aberta, conexão singleton compartilhada já existe, evento
    // "connect" NÃO dispara mais) e (2) no callback "connect" para caso a
    // conexão caia e reconecte.
    //
    // Sem isto: usuário navegava entre tickets via SPA após a primeira visita,
    // nunca entrava na sala do ticket → appMessage update de ack do visto
    // nunca chegava → precisava F5.
    const entrarNaSala = () => {
      try { socket.emit("joinChatBox", ticketId); } catch (_) {}
    };
    try { if (socket.connected) entrarNaSala(); } catch (_) {}
    socket.on("connect", entrarNaSala);

    const onTicket = (data) => {
      // Mesma armadilha do MessagesList: evento "ticket" vai para salas de
      // status/notification compartilhadas. Sem conferir id atualiza o
      // cabeçalho de outro ticket.
      if (data.action === "update") {
        if (Number(data.ticket?.id) !== Number(ticketId)) return;
        setTicket((antigo) => ({ ...antigo, ...data.ticket }));
      }

      // Motivos "status"/"mention" não são deleção real de banco — o ticket
      // continua existindo e quem está lendo não pode ser expulso.
      if (
        data.action === "delete" &&
        data.motivo !== "status" &&
        data.motivo !== "mention"
      ) {
        if (Number(data.ticketId) !== Number(ticketId)) return;
        toast.success("Ticket deleted sucessfully.");
        navigate("/tickets");
      }
    };
    socket.on("ticket", onTicket);

    // Nota interna mencionou o usuário neste mesmo ticket que está aberto.
    // Atualiza flags do cabeçalho em tempo real.
    const onTicketMention = (data) => {
      if (!data) return;
      const ticketMencionado = +(
        data.ticketId ||
        data.mention?.ticketId ||
        0
      );
      if (!ticketMencionado || Number(ticketMencionado) !== Number(ticketId)) return;
      setTicket((antigo) => ({
        ...antigo,
        unreadMentions: Math.max(1, +(antigo.unreadMentions || 0) + 1),
        hasUnreadMentionForUser: true,
        hasMentionForUser: true,
      }));
    };
    socket.on("ticket:mention", onTicketMention);

    const onContact = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };
    socket.on("contact", onContact);

    // A agenda mudou em algum lugar (alguém agendou/editou/cancelou uma
    // mensagem). Só recarrega se for deste atendimento ou do contato dele —
    // assim o aviso de "conversa agendada" aparece/some sem F5.
    const onScheduledMessage = (data) => {
      const mesmoTicket =
        data?.ticketId && Number(data.ticketId) === Number(ticketId);
      const mesmoContato =
        data?.contactId &&
        Number(data.contactId) === Number(contactIdAtualRef.current);
      if (mesmoTicket || mesmoContato) recarregarTicket();
    };
    socket.on("scheduledMessage", onScheduledMessage);

    return () => {
      // NÃO chamar socket.disconnect() — conexão singleton compartilhada por
      // todo o CRM. Disconnect aqui matava a conexão de atendimentos paralelos,
      // chat interno, transferências, visto/ack etc. até o próximo F5.
      try { socket.off("connect", entrarNaSala); } catch (_) {}
      try { socket.off("ticket", onTicket); } catch (_) {}
      try { socket.off("ticket:mention", onTicketMention); } catch (_) {}
      try { socket.off("contact", onContact); } catch (_) {}
      try { socket.off("scheduledMessage", onScheduledMessage); } catch (_) {}
    };
  }, [ticketId, navigate, recarregarTicket]);

  // Ao digitar no celular o teclado cobre a parte de baixo da tela, mas o
  // `h-dvh` do layout continua contando a tela inteira: a caixa de digitar
  // ficava atrás do teclado e o cabeçalho saía por cima. Amarrar a altura
  // desta tela à faixa visível devolve o comportamento de aplicativo de
  // mensagem — cabeçalho colado no topo, caixa de texto colada no teclado e
  // só a conversa rolando no meio.
  const { alturaVisivel, deslocamento, tecladoAberto } = useTecladoVirtual();

  const estiloTeclado = tecladoAberto
    ? {
        height: `${alturaVisivel}px`,
        // O navegador empurra a página para cima para revelar o campo focado;
        // sem devolver esse deslocamento o cabeçalho fica fora da tela.
        transform: `translateY(${deslocamento}px)`,
      }
    : undefined;

  // Encolher a área visível não move o rolamento da conversa: a última
  // mensagem, que estava no fim, some atrás da caixa de texto. Recolar no fim
  // quando o teclado abre (e a cada mudança de altura) mantém à vista o que a
  // pessoa estava lendo.
  useEffect(() => {
    if (!tecladoAberto) return;
    const lista = document.getElementById("messagesList");
    if (lista) lista.scrollTop = lista.scrollHeight;
  }, [tecladoAberto, alturaVisivel]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div
      id="drawer-container"
      className="relative flex h-full w-full overflow-hidden"
      style={estiloTeclado}
    >
      <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-background">
        <TicketHeader loading={loading}>
          <div className="relative flex min-w-0 flex-1 items-center">
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          {/* Etiquetas ficam na fila de ações, e não mais penduradas sobre a
              foto do contato: ali eram 28px sem rótulo por cima do avatar, e
              quem procurava como etiquetar um atendimento concluía que o
              sistema não etiquetava. Fora do TicketInfo porque ele é um
              <button> — botão dentro de botão é HTML inválido e o clique de
              um engole o do outro. */}
          {!loading && ticket.id && <TicketTagsBar ticket={ticket} />}
          {/* Atalho para as imagens da conversa. Fica antes das ações porque
              é leitura — não muda o estado do atendimento. */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setGaleriaAberta(true)}
            title="Arquivos do atendimento"
            aria-label="Arquivos do atendimento"
            className="shrink-0"
          >
            <Files className="h-5 w-5" />
          </Button>
          <TicketActionButtons
            ticket={ticket}
            // Mescla em vez de substituir: a resposta de ações do cabeçalho
            // (ex.: dispensar menção) não carrega o "agendamentoPendente",
            // que só a leitura da conversa calcula — substituir apagaria o
            // aviso de conversa agendada até o próximo recarregamento.
            onTicketChanged={(novo) => setTicket((antigo) => ({ ...antigo, ...novo }))}
          />
        </TicketHeader>

        {/* Aviso de conversa agendada: este atendimento (ou o contato dele) tem
            mensagem agendada para sair. Faixa discreta logo abaixo do cabeçalho
            — informa sem competir com a conversa.

            São dois alvos numa linha só: clicar no texto abre o agendamento
            para ver/editar, e o botão à direita manda a mensagem na hora. Por
            isso a faixa é um <div>, e não um único botão — botão dentro de
            botão não é HTML válido e o clique passaria a disparar os dois. */}
        {ticket.agendamentoPendente && (() => {
          const quando = parseISO(ticket.agendamentoPendente);
          const valido = !Number.isNaN(quando.getTime());
          return (
            <div className="flex w-full min-w-0 items-center gap-2 border-b border-violet-200 bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/40 dark:text-violet-300">
              <button
                type="button"
                onClick={() => setAgendamentoAberto(true)}
                title="Clique para ver a conversa agendada"
                className="flex min-w-0 flex-1 items-center gap-2 text-left transition-opacity hover:opacity-80"
              >
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {valido
                    ? `Conversa agendada para ${format(quando, "dd/MM/yyyy")} às ${format(quando, "HH:mm")}`
                    : "Este atendimento tem conversa agendada"}
                </span>
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide opacity-70">
                  ver
                </span>
              </button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={enviandoAgendamentoAgora}
                onClick={handleEnviarAgendamentoAgora}
                title="Envia esta mensagem agora, sem esperar a hora marcada"
                className="h-6 shrink-0 gap-1 border-violet-300 bg-white/70 px-2 text-[11px] font-semibold text-violet-700 hover:bg-white dark:border-violet-800 dark:bg-violet-900/40 dark:text-violet-200 dark:hover:bg-violet-900/70"
              >
                {enviandoAgendamentoAgora ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Send className="h-3 w-3" />
                )}
                ENVIAR AGORA
              </Button>
            </div>
          );
        })()}

        {/* Modal de agendamento (o mesmo da tela de Agendamentos) aberto em
            modo "ver/editar" pelo clique no aviso acima. O id vem do próprio
            atendimento, então o modal carrega a mensagem certa do servidor. */}
        <ScheduledMessageModal
          open={agendamentoAberto}
          onClose={() => setAgendamentoAberto(false)}
          scheduledMessageId={ticket.agendamentoPendenteId}
          initialTicket={ticket}
          initialContact={contact}
          lockedIds
          onSaved={() => recarregarTicket()}
        />

        <ReplyMessageProvider>
          <ChatOptimisticProvider ticketId={ticketId}>
            <MessagesList
              ticketId={ticketId}
              isGroup={ticket.isGroup}
            ></MessagesList>
            <MessageInput
              ticketStatus={ticket.status}
              isGroup={ticket.isGroup}
              ticket={ticket}
            />
          </ChatOptimisticProvider>
        </ReplyMessageProvider>
      </div>
      <TicketMediaGallery
        open={galeriaAberta}
        onClose={() => setGaleriaAberta(false)}
        ticketId={ticketId}
      />
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        ticket={ticket}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
